import React, { useImperativeHandle, useRef } from 'react';
import PropTypes from 'prop-types';
import './styles.css';
import LoadMask from '../../components/LoadMask';
import LinkBuilder from '../../helpers/link-builder';
import {Role, showNoCandidateProfileDialog} from '../../helpers/user';
import CommentForm from './components/CommentForm';
import CommentItem from './components/CommentItem';

const CommentsBlock = React.forwardRef((props, ref) => {
  const {
    user,
    comments,
    count,
    isFetching,
    onSendComment,
    onRemove,
    onClickLoadButton,
  } = props;

  const textArea = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      textArea.current.focus();
    },
  }));

  const handleReply = name => {
    textArea.current.value = `${name}, ${textArea.current.value}`;
    textArea.current.focus();
  };

  const handleSendComment = comment => {
    if (Role.isCandidate(user)) {
      onSendComment(comment);
    } else {
      showNoCandidateProfileDialog(user);
    }
  };

  return (
    <div className="comments-block">
      {isFetching && <LoadMask relative />}
      <CommentForm
        ref={textArea}
        candidateName={user.name}
        candidatePicture={LinkBuilder.getCandidatePictureSizeLink(
          user.avatar,
          '38x38'
        )}
        maxLength={500}
        disabled={false}
        onSend={handleSendComment}
        isLast={!comments.length}
      />
      <div className="comments-block__list">
        {comments.length < Number(count) && (
          <span className="comments-block__link" onClick={onClickLoadButton}>
            Show previous comments
          </span>
        )}
        {comments.length > 0 &&
          comments.map(comment => (
            <CommentItem
              key={comment.ID}
              user={user}
              id={comment.ID}
              candidateId={comment.CandidateID}
              candidateName={comment.CandidateName}
              candidatePictureUrl={comment.CandidatePictureUrl}
              date={comment.Date}
              text={comment.Text}
              isAllowedViewFullProfile={comment.ViewFullProfile === 'Yes'}
              isAllowedViewShortProfile={comment.ViewShortProfile === 'Yes'}
              isRemoveButtonVisible={comment.Removable === 'Yes'}
              isReplyButtonVisible={comment.ShowReply === 'Yes'}
              onClickReplyHandler={handleReply}
              onClickRemoveHandler={onRemove}
            />
          ))}
      </div>
    </div>
  );
});

CommentsBlock.propTypes = {
  user: PropTypes.object.isRequired,
  comments: PropTypes.arrayOf(
    PropTypes.shape({
      ID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      CandidateID: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
      CandidateName: PropTypes.string.isRequired,
      CandidatePictureUrl: PropTypes.string,
      Date: PropTypes.string.isRequired,
      Text: PropTypes.string.isRequired,
      ViewFullProfile: PropTypes.string.isRequired,
      ViewShortProfile: PropTypes.string.isRequired,
      Removable: PropTypes.string.isRequired,
      ShowReply: PropTypes.string.isRequired,
    })
  ).isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  isFetching: PropTypes.bool,
  onSendComment: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClickLoadButton: PropTypes.func.isRequired,
};

export default CommentsBlock;
